/**
 * Help search: the query field and its ranked results with the landlord's words highlighted (BR-13).
 * An empty query shows nothing here (the caller shows the categories); a query with no results says so
 * and offers the chatbot and contact support.
 */

import { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { HELP_SEARCH_QUERY_MAX_CHARS } from '@resida/api-types/help';
import { Button } from '../ui/button';
import { EmptyState } from '../layout/EmptyState';
import { cn } from '@/lib/utils';
import { useHelpSearch } from '../../lib/api.help';
import { ArticleRow, HelpEyebrow, HelpLoadError, RowsSkeleton } from './ArticleList';
import { highlightParts, queryTokens } from './highlight';

/** Typing pauses this long before a search is sent. */
const SEARCH_DEBOUNCE_MS = 250;

interface HelpSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** The help center's search is larger than the panel's. */
  large?: boolean;
  'data-testid': string;
}

export function HelpSearchField({ value, onChange, placeholder, large, 'data-testid': testId }: HelpSearchFieldProps) {
  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        type="text"
        role="searchbox"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={HELP_SEARCH_QUERY_MAX_CHARS}
        placeholder={placeholder}
        aria-label="Search help"
        data-testid={testId}
        className={cn(
          'w-full rounded-lg border border-input bg-background pl-9 pr-11 text-base text-foreground md:text-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          large ? 'min-h-[52px]' : 'min-h-[44px]',
        )}
      />
      {value !== '' && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          data-testid={`${testId}-clear`}
          className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function Highlighted({ text, tokens }: { text: string; tokens: string[] }) {
  return (
    <>
      {highlightParts(text, tokens).map((part, index) =>
        part.match ? (
          <mark key={index} className="rounded-sm bg-primary/20 px-px text-foreground">
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

interface SearchResultsProps {
  query: string;
  /** In the panel a result opens in place; without it the rows are links to /help/<id>. */
  onSelectArticle?: (articleId: string) => void;
  /** Offers the chatbot; omitted while the chat is switched off. */
  onAsk?: () => void;
  onContactSupport: () => void;
}

export function SearchResults({ query, onSelectArticle, onAsk, onContactSupport }: SearchResultsProps) {
  const [debounced, setDebounced] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const trimmed = debounced.trim();
  const { data, isLoading, isError, isPlaceholderData, refetch } = useHelpSearch(trimmed);
  const tokens = useMemo(() => queryTokens(trimmed), [trimmed]);

  if (trimmed === '') return null;
  if (isLoading) return <RowsSkeleton data-testid="help-search-loading" />;
  if (isError) {
    return (
      <HelpLoadError message="We couldn't search the help articles." onRetry={() => void refetch()} data-testid="help-search-error" />
    );
  }

  const results = data ?? [];
  if (results.length === 0) {
    return (
      <EmptyState
        title={`No results for “${trimmed}”`}
        description="Try different words, or get an answer another way."
        data-testid="help-search-empty"
        className="py-6"
        action={
          <div className="flex flex-col items-stretch gap-2">
            {onAsk && (
              <Button type="button" onClick={onAsk} data-testid="help-search-ask">
                Ask Resida Help instead
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onContactSupport} data-testid="help-search-contact">
              Contact support
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="help-search-results">
      <section
        aria-labelledby="help-search-count"
        className={cn('flex flex-col', isPlaceholderData && 'opacity-60')}
        aria-busy={isPlaceholderData}
      >
        <HelpEyebrow id="help-search-count" data-testid="help-search-count">
          {results.length} {results.length === 1 ? 'result' : 'results'}
        </HelpEyebrow>
        {results.map((result) => (
          <ArticleRow
            key={result.articleId}
            articleId={result.articleId}
            title={<Highlighted text={result.title} tokens={tokens} />}
            excerpt={<Highlighted text={result.excerpt} tokens={tokens} />}
            onSelect={onSelectArticle}
            data-testid={`help-search-result-${result.articleId}`}
          />
        ))}
      </section>
      {onAsk && (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4">
          <span className="text-sm font-semibold text-foreground">Not what you were after?</span>
          <Button type="button" variant="outline" onClick={onAsk} data-testid="help-search-ask">
            Ask Resida Help instead
          </Button>
        </div>
      )}
    </div>
  );
}
